import { useCallback, useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  Ghost,
  Globe,
  Inbox,
  Instagram,
  Mail,
  MessageCircle,
  MessageSquare,
  Phone,
  Search,
  Send,
  User,
  UserPlus,
  type LucideIcon,
} from "lucide-react";
import {
  NavLink,
  Outlet,
  useParams,
  useSearchParams,
} from "react-router-dom";
import type { Lead } from "@lumika/shared";
import {
  Badge,
  Button,
  Input,
  PageError,
  PageLoading,
  cn,
} from "@lumika/ui";
import { adminApi } from "../api/admin";
import { ContactAvatar } from "../components/ContactAvatar";
import { formatDateTime, formatInboxRelativeTime } from "../lib/format";

// Plain-English pipeline labels (kept local to avoid a cross-file shared module).
const LEAD_STATUS_LABEL: Record<string, string> = {
  new: "New",
  working: "Working",
  won: "Won",
  lost: "Lost",
};

// Channel → brand-ish color + icon. Mirrors the App platform inbox.
const CHANNEL_VISUAL: Record<
  string,
  { Icon: LucideIcon; bg: string; fg: string; label: string }
> = {
  telegram:       { Icon: Send,          bg: "bg-sky-500",    fg: "text-white", label: "Telegram" },
  messenger:      { Icon: MessageCircle, bg: "bg-blue-600",   fg: "text-white", label: "Messenger" },
  instagram:      { Icon: Instagram,     bg: "bg-pink-500",   fg: "text-white", label: "Instagram" },
  whatsapp:       { Icon: MessageCircle, bg: "bg-emerald-500",fg: "text-white", label: "WhatsApp" },
  sms:            { Icon: MessageSquare, bg: "bg-violet-500", fg: "text-white", label: "SMS" },
  email:          { Icon: Mail,          bg: "bg-amber-500",  fg: "text-white", label: "Email" },
  voice:          { Icon: Phone,         bg: "bg-slate-500",  fg: "text-white", label: "Voice" },
  web:            { Icon: Globe,         bg: "bg-indigo-500", fg: "text-white", label: "Web" },
};

function ChannelAvatar({
  channel,
  size = "md",
}: {
  channel: string;
  size?: "md" | "xs";
}) {
  const v = CHANNEL_VISUAL[channel] ?? {
    Icon: MessageCircle,
    bg: "bg-muted",
    fg: "text-muted-foreground",
    label: channel,
  };
  const Icon = v.Icon;
  const isXs = size === "xs";
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full",
        isXs ? "h-4 w-4 ring-2 ring-card" : "h-8 w-8",
        v.bg,
        v.fg,
      )}
      aria-label={v.label}
      title={v.label}
    >
      <Icon className={isXs ? "h-2.5 w-2.5" : "h-4 w-4"} />
    </span>
  );
}

// Admin variant of the Podium taxonomy. "Hot" is dropped because the
// hot-lead computation lives in the App platform's /inbox endpoint —
// the admin scope hits the generic /leads endpoint instead.
type InboxView = "all" | "assigned" | "unassigned";

const VIEWS: { key: InboxView; label: string; Icon: LucideIcon }[] = [
  { key: "all", label: "All", Icon: Inbox },
  { key: "assigned", label: "Assigned", Icon: User },
  { key: "unassigned", label: "Unassigned", Icon: UserPlus },
];

const ALLOWED_VIEWS = new Set<InboxView>(VIEWS.map((v) => v.key));

const ANON_STORAGE_KEY = "lumika.admin.inbox.anonymous.v1";

// Initial page; "Load more" pulls the next page driven by the server total.
const PAGE_SIZE = 20;

export function DealershipInboxLayout() {
  const { dealershipId, leadId } = useParams();
  const [params, setParams] = useSearchParams();
  const rawView = (params.get("view") ?? "all") as InboxView;
  const view: InboxView = ALLOWED_VIEWS.has(rawView) ? rawView : "all";
  const search = (params.get("q") ?? "").trim();
  const [showAnonymous, toggleAnonymous] = usePersistedAnonymousFilter(
    ANON_STORAGE_KEY,
    params,
    setParams,
  );
  const rawState = params.get("state") ?? "open";
  const stateFilter: "open" | "closed" =
    rawState === "closed" ? "closed" : "open";

  // Debounce the free-text search 300ms so each keystroke doesn't fire a
  // request; the debounced value drives the server query (and resets paging).
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // The view + state tab + search all map to server-side filters, so paging
  // stays correct: "assigned"/"unassigned" go server-side, free-text search
  // runs against the contact name + preview server-side.
  const conversationsQuery = useInfiniteQuery({
    queryKey: [
      "admin-bdc",
      dealershipId,
      "inbox",
      view,
      showAnonymous,
      stateFilter,
      debouncedSearch,
    ],
    queryFn: ({ pageParam }) =>
      adminApi.listLeads(dealershipId!, {
        includeAnonymous: showAnonymous,
        closed: stateFilter === "closed" ? "true" : "false",
        assignedUserId: view === "unassigned" ? "unassigned" : undefined,
        assigned: view === "assigned" ? true : undefined,
        search: debouncedSearch || undefined,
        page: pageParam,
        pageSize: PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.items.length, 0);
      return loaded < lastPage.total ? allPages.length + 1 : undefined;
    },
    enabled: Boolean(dealershipId),
  });

  // Admin doesn't have a dedicated /users endpoint on the inbox scope;
  // the dealership detail already carries the full user list.
  const detailQuery = useQuery({
    queryKey: ["dealership", dealershipId],
    queryFn: () => adminApi.getDealership(dealershipId!),
    enabled: Boolean(dealershipId),
  });

  // Offset pagination over a live, lastActivityAt-sorted list can
  // re-deliver a row on the next page — dedupe by id so React keys
  // stay unique and threads don't render twice.
  const items = useMemo(() => {
    const flat = conversationsQuery.data?.pages.flatMap((p) => p.items) ?? [];
    const seen = new Set<string>();
    return flat.filter((lead) => {
      if (seen.has(lead.id)) return false;
      seen.add(lead.id);
      return true;
    });
  }, [conversationsQuery.data]);

  // Name + avatar come denormalized on each lead row, so the inbox
  // no longer fetches every contact in the dealership just to label rows.
  const contactName = useMemo(() => {
    const map = new Map<string | null, string>();
    for (const c of items) {
      if (c.contactName) map.set(c.contactId, c.contactName);
    }
    return map;
  }, [items]);

  const contactAvatarById = useMemo(() => {
    const map = new Map<string | null, string | null>();
    for (const c of items) map.set(c.contactId, c.contactAvatarUrl);
    return map;
  }, [items]);

  const usersById = useMemo(() => {
    const m = new Map<string, { displayName: string; initials: string }>();
    for (const u of detailQuery.data?.users ?? []) {
      const name =
        u.displayName ?? [u.firstName, u.lastName].filter(Boolean).join(" ");
      const fallback = name || u.email || "User";
      m.set(u.id, { displayName: fallback, initials: initialsOf(fallback) });
    }
    return m;
  }, [detailQuery.data]);

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = conversationsQuery;

  function setView(next: InboxView) {
    const updated = new URLSearchParams(params);
    updated.set("view", next);
    setParams(updated, { replace: true });
  }

  function setQuery(next: string) {
    const updated = new URLSearchParams(params);
    if (next) updated.set("q", next);
    else updated.delete("q");
    setParams(updated, { replace: true });
  }

  function setStateFilter(next: "open" | "closed") {
    const updated = new URLSearchParams(params);
    if (next === "open") updated.delete("state");
    else updated.set("state", next);
    setParams(updated, { replace: true });
  }

  const showList = !leadId;
  const searchSuffix = params.toString() ? `?${params.toString()}` : "";

  return (
    <div className="grid h-full min-h-0 grid-cols-1 md:grid-cols-[320px_minmax(0,1fr)]">
      <aside
        className={cn(
          "min-h-0 flex-col border-r bg-card md:flex",
          showList ? "flex" : "hidden",
        )}
      >
        <header className="flex flex-col gap-3 border-b px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-base font-semibold tracking-tight">Inbox</h1>
            <Badge variant="outline" className="font-mono text-[10px]">
              {conversationsQuery.data?.pages[0]?.total ?? items.length}
            </Badge>
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={params.get("q") ?? ""}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search threads"
              className="h-9 pl-8"
              aria-label="Search threads"
            />
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {VIEWS.map((v) => {
              const active = view === v.key;
              const Icon = v.Icon;
              return (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => setView(v.key)}
                  aria-pressed={active}
                  className={cn(
                    "flex flex-col items-center justify-center gap-1 rounded-lg border px-1.5 py-2 text-[10px] font-medium transition-colors",
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-muted-foreground hover:bg-muted",
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span className="truncate">{v.label}</span>
                </button>
              );
            })}
          </div>
          {/* Status tabs: Open (default) | Done — mirrors the App platform.
              Recency is a sort, not a status, so there's no "Recent" tab. */}
          <div className="-mx-1 flex items-center gap-0.5 overflow-x-auto px-1 border-b">
            {(
              [
                { key: "open" as const, label: "Open" },
                { key: "closed" as const, label: "Done" },
              ]
            ).map((t) => {
              const active = stateFilter === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setStateFilter(t.key)}
                  aria-pressed={active}
                  className={cn(
                    "shrink-0 border-b-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition-colors",
                    active
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={toggleAnonymous}
              aria-pressed={showAnonymous}
              className={cn(
                showAnonymous &&
                  "border-primary/40 bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary",
              )}
            >
              <Ghost className="h-4 w-4" />
              Anonymous
            </Button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto">
          {conversationsQuery.isLoading ? (
            <PageLoading message="Loading inbox…" className="min-h-[20vh]" />
          ) : conversationsQuery.isError ? (
            <PageError
              title="Couldn't load leads"
              description="Try refreshing the page."
              className="min-h-[20vh]"
            />
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center text-sm text-muted-foreground">
              <Inbox className="h-5 w-5" />
              <span>
                {search
                  ? "No threads match your search."
                  : `No leads in "${view}".`}
              </span>
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((lead) => (
                <LeadRow
                  key={lead.id}
                  lead={lead}
                  selected={lead.id === leadId}
                  contactName={
                    lead.displayName ??
                    contactName.get(lead.contactId) ??
                    lead.fallbackLabel ??
                    "Anonymous"
                  }
                  contactAvatarUrl={contactAvatarById.get(lead.contactId) ?? null}
                  assignedUser={
                    lead.assignedUserId
                      ? usersById.get(lead.assignedUserId) ?? null
                      : null
                  }
                  searchSuffix={searchSuffix}
                />
              ))}
              {hasNextPage && (
                <li className="p-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    disabled={isFetchingNextPage}
                    onClick={() => void fetchNextPage()}
                  >
                    {isFetchingNextPage ? "Loading…" : "Load more"}
                  </Button>
                </li>
              )}
            </ul>
          )}
        </div>
      </aside>
      <section className={cn("min-w-0 overflow-y-auto md:block", leadId ? "block" : "hidden md:block")}>
        {leadId ? (
          <Outlet />
        ) : (
          <div className="hidden h-full flex-col items-center justify-center gap-3 px-6 text-center text-sm text-muted-foreground md:flex">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <MessageSquare className="h-5 w-5" />
            </div>
            <div className="grid gap-1">
              <h2 className="text-base font-semibold text-foreground">Select a lead</h2>
              <p className="max-w-sm text-muted-foreground">
                Pick a thread on the left to view its activity, customer profile, and take action.
              </p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function LeadRow({
  lead,
  selected,
  contactName,
  contactAvatarUrl,
  assignedUser,
  searchSuffix,
}: {
  lead: Lead;
  selected: boolean;
  contactName: string;
  contactAvatarUrl: string | null;
  assignedUser: { displayName: string; initials: string } | null;
  searchSuffix: string;
}) {
  const isUnread = lead.unreadAt !== null;

  return (
    <li>
      <NavLink
        to={`${lead.id}${searchSuffix}`}
        className={cn(
          "flex items-center gap-2.5 border-l-2 px-3 py-2.5 text-sm transition-colors",
          selected
            ? "border-l-primary bg-primary/5"
            : "border-l-transparent hover:bg-muted/60",
        )}
      >
        <ContactAvatar
          name={contactName}
          avatarUrl={contactAvatarUrl}
          badge={<ChannelAvatar channel={lead.primaryChannel} size="xs" />}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-center justify-between gap-2">
            <span
              className={cn(
                "min-w-0 truncate text-[13px] leading-tight",
                isUnread ? "font-semibold text-foreground" : "font-medium text-foreground",
              )}
            >
              {contactName}
            </span>
            <span
              className={cn(
                "shrink-0 text-[10px] uppercase tracking-wide",
                isUnread ? "font-semibold text-foreground" : "text-muted-foreground",
              )}
              title={formatDateTime(lead.lastActivityAt)}
            >
              {formatInboxRelativeTime(lead.lastActivityAt)}
            </span>
          </div>
          <p
            className={cn(
              "line-clamp-1 text-xs",
              isUnread ? "font-medium text-foreground" : "text-muted-foreground",
            )}
          >
            {lead.lastActivityPreview ?? "(no preview)"}
          </p>
          {(assignedUser || lead.status) && (
            // Assignee + one pipeline-status chip — mirrors the App's
            // decluttered row (type and a redundant "closed" label dropped).
            <div className="mt-0.5 flex flex-wrap items-center gap-1">
              {assignedUser ? (
                <span
                  className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[8px] font-bold uppercase text-primary"
                  title={`Assigned to ${assignedUser.displayName}`}
                >
                  {assignedUser.initials}
                </span>
              ) : null}
              {lead.status ? (
                <span
                  className="inline-flex items-center rounded-full bg-muted px-1.5 py-px text-[9px] font-semibold uppercase leading-tight text-muted-foreground"
                  title={`Pipeline: ${LEAD_STATUS_LABEL[lead.status] ?? lead.status}`}
                >
                  {LEAD_STATUS_LABEL[lead.status] ?? lead.status}
                </span>
              ) : null}
            </div>
          )}
        </div>
        {isUnread ? (
          <span
            aria-label="Unread"
            className="ml-1 inline-block h-2 w-2 shrink-0 self-center rounded-full bg-primary"
          />
        ) : null}
      </NavLink>
    </li>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

// "Show anonymous" quick-filter. Persists last toggle in localStorage so
// it survives a page refresh — same UX as the App platform inbox.
function usePersistedAnonymousFilter(
  storageKey: string,
  searchParams: URLSearchParams,
  setSearchParams: (
    next: URLSearchParams,
    options?: { replace?: boolean },
  ) => void,
): [boolean, () => void] {
  const [fallback] = useState(() => loadBoolPref(storageKey));
  const param = searchParams.get("anon");
  const showAnonymous = param !== null ? param === "1" : fallback;
  const toggle = useCallback(() => {
    const next = !showAnonymous;
    saveBoolPref(storageKey, next);
    const updated = new URLSearchParams(searchParams);
    updated.set("anon", next ? "1" : "0");
    setSearchParams(updated, { replace: true });
  }, [showAnonymous, searchParams, setSearchParams, storageKey]);
  return [showAnonymous, toggle];
}

function loadBoolPref(storageKey: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(storageKey) === "1";
  } catch {
    return false;
  }
}

function saveBoolPref(storageKey: string, value: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, value ? "1" : "0");
  } catch {
    // localStorage may be unavailable in some browsers — the toggle
    // still works for the rest of this session.
  }
}
